// frontend/src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { SkeletonRow } from './SkeletonRow';
import { db } from '../services/db';
import { formatDisplayDate } from '../utils/dateHelpers';

async function computeFromDexie(weaningDays = 21) {
  const now = new Date();
  const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
  // Per-user weaning age (C8). This offline fast path had its own hard-coded 21,
  // so before the API answered, the dashboard showed a different set of litters
  // than it did a moment later for any lab that does not wean at 21 days.
  const weaningCutoff = new Date(now - weaningDays * 24 * 60 * 60 * 1000).toISOString();

  const [activeMice, activeCages, allLitters] = await Promise.all([
    db.mice.where('status').equals('Active').toArray(),
    db.cages.where('status').equals('Active').toArray(),
    // C6 step 3: read the status bucket through the index rather than scanning
    // the whole table. Archived litters accumulate indefinitely and are never
    // used here, so this bounds the work by live litters instead of all-time.
    db.litters.where('status').notEqual('Archived').toArray(),
  ]);

  if (activeMice.length === 0 && activeCages.length === 0 && allLitters.length === 0) {
    return null; // Dexie empty — first launch before sync
  }

  // allLitters is already Archived-free (see the indexed query above).
  const activeLitters = allLitters;
  const littersDueArr = activeLitters.filter(
    l => l.status !== 'Weaned' && l.date_of_birth && l.date_of_birth <= weaningCutoff
  ).map(l => ({
    ...l,
    days_old: Math.floor((now - new Date(l.date_of_birth)) / (24 * 60 * 60 * 1000)),
  }));

  const lastLitterByCage = {};
  allLitters.forEach(l => {
    if (l.cage_id && l.date_of_birth) {
      if (!lastLitterByCage[l.cage_id] || l.date_of_birth > lastLitterByCage[l.cage_id]) {
        lastLitterByCage[l.cage_id] = l.date_of_birth;
      }
    }
  });

  const breedingCagesToCheckArr = activeCages
    .filter(c => c.breeding_status === 'Breeding')
    .filter(c => {
      const last = lastLitterByCage[c.id];
      if (!last) return true;
      return Math.floor((now - new Date(last)) / (24 * 60 * 60 * 1000)) > 60;
    })
    .map(c => {
      const last = lastLitterByCage[c.id];
      return {
        ...c,
        last_litter_date: last || null,
        days_since_last_litter: last
          ? Math.floor((now - new Date(last)) / (24 * 60 * 60 * 1000))
          : null,
      };
    })
    .sort((a, b) => (b.days_since_last_litter ?? Infinity) - (a.days_since_last_litter ?? Infinity))
    .slice(0, 20);

  const recentMiceArr = activeMice
    .filter(m => m.date_of_birth && m.date_of_birth >= sevenDaysAgo)
    .sort((a, b) => (b.date_of_birth || '') > (a.date_of_birth || '') ? 1 : -1)
    .slice(0, 20);

  return {
    stats: {
      active_mice: activeMice.length,
      active_cages: activeCages.length,
      active_litters: activeLitters.length,
      breeding_cages: activeCages.filter(c => c.breeding_status === 'Breeding').length,
      male_mice: activeMice.filter(m => m.sex === 'Male').length,
      female_mice: activeMice.filter(m => m.sex === 'Female').length,
      mice_born_last_7d: activeMice.filter(m => m.date_of_birth && m.date_of_birth >= sevenDaysAgo).length,
      litters_due_weaning: littersDueArr.length,
      breeding_cages_to_check: breedingCagesToCheckArr.length,
    },
    littersDue: littersDueArr,
    recentMice: recentMiceArr,
    breedingCagesToCheck: breedingCagesToCheckArr,
  };
}

function StatCard({ label, value, color, sublabel }) {
  return (
    <div className="dashboard-stat-card" style={{ borderTop: `4px solid ${color}` }}>
      <div className="dashboard-stat-value">{value ?? '—'}</div>
      <div className="dashboard-stat-label">{label}</div>
      {sublabel && <div className="dashboard-stat-sublabel">{sublabel}</div>}
    </div>
  );
}

export default function Dashboard({ onNavigate, activeView }) {
  const { token, user } = useAuth();
  const toast = useToast();
  const [stats, setStats] = useState(null);
  const [littersDue, setLittersDue] = useState([]);
  const [recentMice, setRecentMice] = useState([]);
  const [breedingCagesToCheck, setBreedingCagesToCheck] = useState([]);
  const [upcomingBirths, setUpcomingBirths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token || activeView !== 'dashboard') return;

    const fetchAll = async () => {
      setLoading(true);

      // Show cached Dexie data immediately (instant on return visits).
      const dexieResult = await computeFromDexie(user?.weaning_days || 21);
      if (dexieResult) {
        setStats(dexieResult.stats);
        setLittersDue(dexieResult.littersDue);
        setRecentMice(dexieResult.recentMice);
        setBreedingCagesToCheck(dexieResult.breedingCagesToCheck);
        setLoading(false);
      }

      if (!navigator.onLine) return; // Already showing local data, nothing more to do.

      // Refresh from API in background (updates stale Dexie data or populates
      // first load). One request, not five: /summary runs the same five queries
      // under Promise.allSettled server-side, so a single failing slice comes
      // back as null/[] instead of taking the other four down with it.
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/dashboard/summary`);
        // A degraded stats slice comes back null. Leave whatever Dexie already
        // put on screen rather than blanking the cards, and say so — silently
        // showing stale numbers next to fresh tables is the one failure mode
        // this endpoint could introduce.
        if (data.stats) {
          setStats(data.stats);
          setError('');
        } else if (dexieResult) {
          toast.warn('Some dashboard figures could not be refreshed.');
        } else {
          setError('Failed to load dashboard data.');
        }
        setLittersDue(data.littersDue || []);
        setRecentMice(data.recentMice || []);
        setBreedingCagesToCheck(data.breedingCagesToCheck || []);
        setUpcomingBirths(data.upcomingBirths || []);
      } catch (err) {
        // Two different failures, two different affordances. With no cached
        // data the dashboard is blank, so the explanation has to persist —
        // a toast would fade and leave an unexplained empty page. With cached
        // data the page is usable but stale, and that only warrants a toast;
        // this branch previously said nothing at all.
        if (!dexieResult) {
          setError('Failed to load dashboard data.');
        } else {
          toast.warn('Showing cached data — could not reach the server.');
        }
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    // `toast` is deliberately not a dependency: the provider rebuilds the
    // object on every render, so listing it would re-run this effect — and
    // re-fetch the whole dashboard — each time any toast appears or expires.
  }, [token, activeView, user?.weaning_days]);

  // Em-dash placeholder rather than the helper's default 'N/A' — the dashboard
  // tables read better with a blank-looking cell.
  const formatDate = (dateStr) => formatDisplayDate(dateStr, '—');

  return (
    <div className="dashboard-container">
      <h2 className="dashboard-title">Colony Overview</h2>

      {error && <div className="error">{error}</div>}

      {/* ── Stat Cards ───────────────────────────────────────────────────── */}
      {loading ? (
        <SkeletonRow count={3} />
      ) : stats ? (
        <>
          <div className="dashboard-stats-grid">
            <StatCard label="Active Mice"     value={stats.active_mice}    color="#4CAF50" />
            <StatCard label="Active Cages"    value={stats.active_cages}   color="#2196F3" />
            <StatCard label="Active Litters"  value={stats.active_litters} color="#FF9800" />
            <StatCard label="Breeding Cages"  value={stats.breeding_cages} color="#9C27B0"
              sublabel={`of ${stats.active_cages} total`} />
            <StatCard label="Male Mice"       value={stats.male_mice}      color="#03A9F4" />
            <StatCard label="Female Mice"     value={stats.female_mice}    color="#E91E63" />
            <StatCard
              label="Litters Due Weaning"
              value={stats.litters_due_weaning}
              color={stats.litters_due_weaning > 0 ? '#F44336' : '#4CAF50'}
              sublabel={stats.litters_due_weaning > 0 ? 'Action needed' : 'All up to date'}
            />
            <StatCard
              label="Breeding Cages to Check"
              value={stats.breeding_cages_to_check}
              color={stats.breeding_cages_to_check > 0 ? '#F44336' : '#4CAF50'}
              sublabel={stats.breeding_cages_to_check > 0 ? 'No litter in 60+ days' : 'All active'}
            />
          </div>

          {/* ── Litters Due for Weaning ──────────────────────────────────── */}
          {littersDue.length > 0 && (
            <section className="dashboard-section">
              <h3 className="dashboard-section-title">
                Litters Due for Weaning
                <span className="dashboard-badge dashboard-badge-danger">{littersDue.length}</span>
              </h3>
              <div className="dashboard-table-wrapper">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Cage</th>
                      <th>Born</th>
                      <th>Days Old</th>
                      <th>Pups</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {littersDue.map(l => (
                      <tr key={l.id}>
                        <td>{l.display_cage_number || '—'}</td>
                        <td>{formatDate(l.date_of_birth)}</td>
                        <td>
                          <span className="dashboard-badge dashboard-badge-danger">
                            {l.days_old}d
                          </span>
                        </td>
                        <td>{l.initial_pup_count ?? '—'}</td>
                        <td>
                          <button
                            className="dashboard-action-btn"
                            onClick={() => onNavigate && onNavigate('litters')}
                          >
                            View →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Breeding Cages to Check ──────────────────────────────────── */}
          {breedingCagesToCheck.length > 0 && (
            <section className="dashboard-section">
              <h3 className="dashboard-section-title">
                Breeding Cages to Check
                <span className="dashboard-badge dashboard-badge-danger">{breedingCagesToCheck.length}</span>
              </h3>
              <div className="dashboard-table-wrapper">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Cage</th>
                      <th>Last Litter</th>
                      <th>Days Since</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {breedingCagesToCheck.map(c => (
                      <tr key={c.id}>
                        <td>{c.display_cage_number || '—'}</td>
                        <td>{formatDate(c.last_litter_date)}</td>
                        <td>
                          <span className="dashboard-badge dashboard-badge-danger">
                            {c.days_since_last_litter != null ? `${c.days_since_last_litter}d` : 'None'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="dashboard-action-btn"
                            onClick={() => onNavigate && onNavigate('cages', { search: `cage:${c.display_cage_number}` })}
                          >
                            View →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Upcoming Litters ──────────────────────────────────────────── */}
          {upcomingBirths.length > 0 && (
            <section className="dashboard-section">
              <h3 className="dashboard-section-title">
                Upcoming Litters
                <span className="dashboard-badge dashboard-badge-info">{upcomingBirths.length}</span>
              </h3>
              <div className="dashboard-table-wrapper">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Cage</th>
                      <th>Mother</th>
                      <th>Due Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingBirths.map(birth => (
                      <tr key={birth.id}>
                        <td>{birth.display_cage_number || '—'}</td>
                        <td>{birth.mother_tag || '—'}</td>
                        <td>
                          <span className="dashboard-badge dashboard-badge-info">
                            {formatDate(birth.expected_due_date)} ({birth.days_until_due}d)
                          </span>
                        </td>
                        <td>
                          <button
                            className="dashboard-action-btn"
                            onClick={() => onNavigate && onNavigate('cages', { search: `cage:${birth.display_cage_number}` })}
                          >
                            View →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Recently Added Mice ──────────────────────────────────────── */}
          {recentMice.length > 0 && (
            <section className="dashboard-section">
              <h3 className="dashboard-section-title">Recently Added Mice (Last 7 Days)</h3>
              <div className="dashboard-table-wrapper">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Tag</th>
                      <th>Sex</th>
                      <th>Born</th>
                      <th>Cage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentMice.map(m => (
                      <tr key={m.id}>
                        <td>{m.physical_tag || '—'}</td>
                        <td>{m.sex === 'Male' ? 'M' : 'F'}</td>
                        <td>{formatDate(m.date_of_birth)}</td>
                        <td>{m.display_cage_number || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {littersDue.length === 0 && recentMice.length === 0 && breedingCagesToCheck.length === 0 && upcomingBirths.length === 0 && (
            <div className="empty-state" style={{ marginTop: '24px' }}>
              <p>Your colony is all caught up!</p>
              <p>No litters are due for weaning, no breeding cages need attention, no upcoming litters, and no mice were added in the last 7 days.</p>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
